import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, CARD_SECTION_TITLE_CLASS, Modal } from '@safetyhub/ui';
import { useAuthStore } from '../stores/auth';
import { organizationsApi, extractApiError, getMemberKindLabel, getMemberKindBadgeTone } from '../lib/organizations';
import { useToastStore } from '../stores/toast';
import { CATEGORY_OPTIONS } from '../lib/organizations';

export default function OrganizationInfo() {
  const navigate = useNavigate();
  const { currentMembership } = useAuthStore();
  const { showToast } = useToastStore();

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  if (!currentMembership) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <p className="text-ink-500">조직 정보를 불러올 수 없습니다.</p>
      </div>
    );
  }

  const { company, companyId, createdAt } = currentMembership;
  /**
   * 조직 탈퇴 차단 축 — P6-b: 옛 `role === 'OWNER'` → 소유자 **성질**(`isOwner`).
   * 마지막 소유자 보호는 권한 key로 표현되지 않는 판정이라 성질이 정본이다(`usePermissions` doc).
   * P7-b-2부터 아래 역할 Badge도 같은 값을 읽는다(옛 `role` 라벨 축이 컬럼과 함께 사라져 라벨·tone이 한 값에서 나온다).
   */
  const isOwner = currentMembership.isOwner === true;
  const memberKind = isOwner ? 'owner' : 'member';

  const handleLeave = async () => {
    if (!companyId) return;

    setIsLeaving(true);
    try {
      await organizationsApi.leave(companyId);
      setShowLeaveModal(false);

      // 탈퇴 성공: 서버에서 ActionResultHelper를 통해 토스트/알림 전송
      // WebSocketProvider의 handleActionResult에서 처리됨 (사용자 정보 갱신 및 조직 전환 포함)

      navigate('/');
    } catch (error) {
      const apiError = extractApiError(error);
      showToast(apiError.message, 'error');
    } finally {
      setIsLeaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };


  return (
    <div>
      <h1 className="mb-6 text-[26px] font-extrabold tracking-tight text-ink-900">조직 정보</h1>

      {/* 카드는 한 장뿐이라 컬럼으로 쪼갤 게 없다 — 대신 카드 안의 세 구획을 넓은 화면에서
          나란히 펼치고, 구분선을 가로에서 세로로 돌려 전폭을 채운다. */}
      <Card padding="none" radius="lg">
        <div className="lg:grid lg:grid-cols-3">
        {/* 조직 기본 정보 */}
        <div className="p-6 border-b border-line lg:border-b-0 lg:border-r">
          <h2 className={`${CARD_SECTION_TITLE_CLASS} mb-4`}>기본 정보</h2>
          <dl className="space-y-4">
            <div className="flex justify-between">
              <dt className="text-sm text-ink-500">조직명</dt>
              <dd className="text-sm font-medium text-ink-900">{company.name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-ink-500">조직 유형</dt>
              <dd className="text-sm font-medium text-ink-900">
                {CATEGORY_OPTIONS.find((o) => o.value === company.category)?.label ?? company.category}
              </dd>
            </div>
          </dl>
        </div>

        {/* 내 멤버십 정보 */}
        <div className="p-6 border-b border-line lg:border-b-0 lg:border-r">
          <h2 className={`${CARD_SECTION_TITLE_CLASS} mb-4`}>내 멤버십</h2>
          <dl className="space-y-4">
            <div className="flex justify-between">
              <dt className="text-sm text-ink-500">역할</dt>
              <dd className="text-sm font-medium text-ink-900">
                <Badge tone={getMemberKindBadgeTone(memberKind)}>
                  {getMemberKindLabel(memberKind)}
                </Badge>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-ink-500">가입일</dt>
              <dd className="text-sm font-medium text-ink-900">{formatDate(createdAt)}</dd>
            </div>
          </dl>
        </div>

        {/* 조직 탈퇴 */}
        <div className="p-6">
          <h2 className={`${CARD_SECTION_TITLE_CLASS} mb-4`}>조직 탈퇴</h2>
          {isOwner ? (
            <div className="bg-amber-50 border border-amber-200 rounded-card p-4">
              <div className="flex">
                <WarningIcon className="h-5 w-5 text-amber-400 flex-shrink-0" />
                <div className="ml-3">
                  <p className="text-sm text-amber-700">
                    조직소유자는 조직에서 탈퇴할 수 없습니다.
                  </p>
                  <p className="text-sm text-amber-600 mt-1">
                    탈퇴하려면 먼저 다른 멤버에게 조직소유자 권한을 이전해주세요.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-ink-600 mb-4">
                조직에서 탈퇴하면 해당 조직의 현장, 점검, 보고서 등에 더 이상 접근할 수 없습니다.
              </p>
              <button
                onClick={() => setShowLeaveModal(true)}
                className="px-4 py-2 text-sm font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-control hover:bg-rose-100 transition-colors"
              >
                조직 탈퇴
              </button>
            </>
          )}
        </div>
        </div>
      </Card>

      {/* 탈퇴 확인 모달 — 아이콘+제목이 본문 머리라 base 헤더를 쓰지 않는다.
          종전에 오버레이 클릭 닫기가 없었으므로 그대로 끈다(Esc는 base가 새로 준다).
          탈퇴 처리 중에는 닫는 길을 전부 막는다 — 종전에도 취소가 `isLeaving`에서 비활성이었다. */}
      <Modal
        open={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        ariaLabel="조직 탈퇴"
        size="md"
        showCloseButton={false}
        closeOnOverlay={false}
        closeOnEsc={!isLeaving}
        closeLocked={isLeaving}
        footer={
          <>
            <Button type="button" onClick={() => setShowLeaveModal(false)} disabled={isLeaving}>
              취소
            </Button>
            <Button type="button" variant="danger" onClick={handleLeave} disabled={isLeaving}>
              {isLeaving ? '처리 중...' : '탈퇴'}
            </Button>
          </>
        }
      >
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-rose-100 flex items-center justify-center">
                <WarningIcon className="h-6 w-6 text-rose-600" />
              </div>
              <h3 className="ml-3 text-lg font-semibold text-ink-900">
                조직 탈퇴
              </h3>
            </div>

            <p className="text-sm text-ink-600 mb-2">
              <strong>{company.name}</strong>에서 탈퇴하시겠습니까?
            </p>
            <p className="text-sm text-ink-500">
              탈퇴 후에는 해당 조직의 모든 데이터에 접근할 수 없습니다.
              다시 참여하려면 조직 관리자의 초대가 필요합니다.
            </p>
      </Modal>
    </div>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}
